import { z } from 'zod';
import { TRACKING_PHASES } from '../constants/index.js';
import { latLngSchema, uuidSchema } from './common.js';
/**
 * What the app is asked to agree to, and what it has agreed to.
 *
 * The text travels with the version so the device shows exactly what the
 * server will record, and the server records exactly what was shown — not a
 * reference to a row that can later be edited.
 */
export const trackingConsentPromptSchema = z.object({
    version: z.number().int(),
    locale: z.string(),
    text: z.string(),
    /** True when this user has already accepted this version. */
    accepted: z.boolean(),
    acceptedAt: z.string().nullable(),
    /** Which phases are actually recorded, so the app can say so. */
    trackedPhases: z.array(z.enum(TRACKING_PHASES)),
});
export const acceptTrackingConsentSchema = z.object({
    /**
     * Echoed back by the client. A mismatch means the text changed between
     * showing and accepting, and the acceptance is refused rather than recorded
     * against text the person never read.
     */
    version: z.number().int(),
    locale: z.string().min(2).max(8),
});
/** One sample, as the device recorded it. */
export const locationPingInputSchema = z.object({
    /** The device's id for this sample; a repeated upload is ignored. */
    clientUuid: uuidSchema,
    phase: z.enum(TRACKING_PHASES),
    /** Required for `on_route`, forbidden for `on_shift`. */
    routeId: uuidSchema.nullish(),
    location: latLngSchema,
    accuracyMeters: z.number().min(0).max(100_000).nullish(),
    speedMetersPerSecond: z.number().min(0).max(200).nullish(),
    headingDegrees: z.number().min(0).max(360).nullish(),
    /** When the device was there, ISO 8601 with an offset. */
    recordedAt: z.string().datetime({ offset: true }),
});
/**
 * Samples arrive in batches: a phone with no signal keeps recording and uploads
 * the backlog when it can.
 */
export const uploadLocationsSchema = z.object({
    pings: z.array(locationPingInputSchema).min(1).max(500),
});
/**
 * What the server did with a batch.
 *
 * `rejected` carries a reason per sample rather than failing the whole upload:
 * one bad reading must not cost the driver the rest of the batch, and the app
 * needs to know not to retry those.
 */
export const uploadLocationsResultSchema = z.object({
    accepted: z.number().int(),
    duplicates: z.number().int(),
    rejected: z.array(z.object({
        clientUuid: uuidSchema,
        reasonKey: z.string(),
    })),
});
/**
 * Options for fabricating a track along a route's own plan.
 *
 * A demonstration aid, not a feature of the product: the phone being shown is
 * nowhere near the customers, so without this nothing about tracking —
 * geofence arrival, the map, the recorded distance — can be shown without
 * driving a van. Every field has a usable default, so the app can ask for a
 * track with an empty body.
 */
export const simulateTrackSchema = z.object({
    speedKmh: z.number().min(1).max(130).default(30),
    intervalSeconds: z.number().int().min(5).max(120).default(15),
    /** Seconds the van stands still at each stop; a delivery, so the geofence sees a visit. */
    dwellSeconds: z.number().int().min(0).max(3600).default(180),
    noiseMeters: z.number().min(0).max(100).default(10),
    accuracyMeters: z.number().min(1).max(500).default(12),
    /**
     * How far off the plan to wander, so deviation detection has something to
     * find. Zero — the default — drives the plan, which is what a demonstration
     * of a route going well needs.
     */
    deviateMeters: z.number().min(0).max(5000).default(0),
    deviateSeconds: z.number().int().min(0).max(7200).default(600),
});
/** What was fabricated and what the ingestion path did with it. */
export const simulateTrackResultSchema = z.object({
    generated: z.number().int(),
    /** Samples already on the route that this run threw away; see D119. */
    replaced: z.number().int(),
    accepted: z.number().int(),
    duplicates: z.number().int(),
    rejected: z.array(z.object({ clientUuid: uuidSchema, reasonKey: z.string() })),
    /** The window the track covers, which ends at the moment it was asked for. */
    fromAt: z.string(),
    toAt: z.string(),
});
/** A recorded position, as the panel reads it back. */
export const locationPingSchema = z.object({
    id: uuidSchema,
    userId: uuidSchema,
    userName: z.string().nullable(),
    vehicleId: uuidSchema.nullable(),
    vehiclePlate: z.string().nullable(),
    routeId: uuidSchema.nullable(),
    phase: z.enum(TRACKING_PHASES),
    location: latLngSchema,
    accuracyMeters: z.number().nullable(),
    speedMetersPerSecond: z.number().nullable(),
    headingDegrees: z.number().nullable(),
    recordedAt: z.string(),
    receivedAt: z.string(),
});
/** A stretch where the van was not on its planned line. */
export const deviationSegmentSchema = z.object({
    fromAt: z.string(),
    toAt: z.string(),
    durationSeconds: z.number().int(),
    maxDistanceMeters: z.number().int(),
    sampleCount: z.number().int(),
});
/**
 * One route's recorded track.
 *
 * `clockSkewSeconds` is the largest gap seen between a sample's device time and
 * the moment the server received it, after allowing for the upload. It is
 * reported rather than silently corrected: a phone whose clock is an hour out
 * produces a track that looks wrong, and the person reading it should be told
 * why instead of being shown adjusted data.
 */
export const routeTrackSchema = z.object({
    routeId: uuidSchema,
    pings: z.array(locationPingSchema),
    distanceMeters: z.number().int(),
    firstAt: z.string().nullable(),
    lastAt: z.string().nullable(),
    clockSkewSeconds: z.number().int().nullable(),
    /**
     * Derived against the plan as it stands now, not as it stood during the
     * drive. Null when the route has no planned line, which is different from an
     * empty list: "nothing to compare against" is not "followed the plan".
     */
    deviations: z.array(deviationSegmentSchema).nullable(),
    /** Furthest any sample strayed from the plan, metres. */
    maxDistanceFromPathMeters: z.number().int().nullable(),
    /**
     * True when the plan was recomputed after the last sample, so the deviations
     * above are measured against a line the van never had.
     */
    planChangedAfterTrack: z.boolean(),
});
//# sourceMappingURL=tracking.js.map